import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, authAPI, RegisterRequest, LoginRequest } from '../services/api';

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginRequest) => Promise<{ success: boolean; message: string; verification_required?: boolean }>;
  register: (userData: RegisterRequest) => Promise<{ success: boolean; message: string; verification_required?: boolean }>;
  logout: () => void;
  verifyPhone: (phone: string, code: string) => Promise<{ success: boolean; message: string }>;
  resendVerificationCode: (phone: string) => Promise<{ success: boolean; message: string }>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const storedToken = localStorage.getItem('genlend_token');
        const storedUser = localStorage.getItem('genlend_user');

        if (storedToken && storedUser) {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        // Clear corrupted data
        localStorage.removeItem('genlend_token');
        localStorage.removeItem('genlend_user');
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = async (credentials: LoginRequest): Promise<{ success: boolean; message: string; verification_required?: boolean }> => {
    try {
      setLoading(true);
      const response = await authAPI.login(credentials);
      
      // Store token and user data
      localStorage.setItem('genlend_token', response.token);
      localStorage.setItem('genlend_user', JSON.stringify(response.user));
      
      setToken(response.token);
      setUser(response.user);

      return {
        success: true,
        message: response.message,
        verification_required: response.verification_required
      };
    } catch (error: any) {
      console.error('Login error:', error);
      // Explicitly clear any stale auth state on login failure
      // This ensures user stays on login screen and doesn't get redirected
      localStorage.removeItem('genlend_token');
      localStorage.removeItem('genlend_user');
      setToken(null);
      setUser(null);
      
      return {
        success: false,
        message: error.response?.data?.message || error.response?.data?.error || 'Login failed. Please check your credentials and try again.'
      };
    } finally {
      setLoading(false);
    }
  };

  // Register function
  const register = async (userData: RegisterRequest): Promise<{ success: boolean; message: string; verification_required?: boolean }> => {
    try {
      setLoading(true);
      const response = await authAPI.register(userData);
      
      // Store token and user data
      localStorage.setItem('genlend_token', response.token);
      localStorage.setItem('genlend_user', JSON.stringify(response.user));
      
      setToken(response.token);
      setUser(response.user);

      return {
        success: true,
        message: response.message,
        verification_required: response.verification_required
      };
    } catch (error: any) {
      console.error('Registration error:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          (error.response?.status === 409 
                            ? 'A user with this phone number or SA ID already exists. Please try logging in instead.' 
                            : 'Registration failed. Please try again.');
      return {
        success: false,
        message: errorMessage
      };
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('genlend_token');
    localStorage.removeItem('genlend_user');
    setToken(null);
    setUser(null);
  };

  // Verify phone function
  const verifyPhone = async (phone: string, code: string): Promise<{ success: boolean; message: string }> => {
    try {
      setLoading(true);
      const response = await authAPI.verifyPhone({ phone_number: phone, verification_code: code });
      
      // Update user data
      localStorage.setItem('genlend_user', JSON.stringify(response.user));
      setUser(response.user);

      return {
        success: true,
        message: response.message
      };
    } catch (error: any) {
      console.error('Phone verification error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Verification failed. Please try again.'
      };
    } finally {
      setLoading(false);
    }
  };

  // Resend verification code
  const resendVerificationCode = async (phone: string): Promise<{ success: boolean; message: string }> => {
    try {
      setLoading(true);
      const response = await authAPI.resendVerificationCode(phone);
      
      return {
        success: true,
        message: response.message
      };
    } catch (error: any) {
      console.error('Resend verification error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to resend verification code.'
      };
    } finally {
      setLoading(false);
    }
  };

  // Refresh user profile
  const refreshProfile = async (): Promise<void> => {
    try {
      if (!token) return;
      
      const response = await authAPI.getProfile();
      localStorage.setItem('genlend_user', JSON.stringify(response.user));
      setUser(response.user);
    } catch (error) {
      console.error('Error refreshing profile:', error);
      // If profile fetch fails, might be token issue
      logout();
    }
  };

  const value: AuthContextType = {
    user,
    token,
    loading,
    isAuthenticated: !!token && !!user,
    login,
    register,
    logout,
    verifyPhone,
    resendVerificationCode,
    refreshProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;


